import { faCheckCircle, faExclamationCircle, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import Configs from '../../../../config';
import { MiscContext } from '../../../../context/misc.context';
import Enums from '../../../../enums';
import GlobalHttpErrorHandler from '../../../../errors/globalHttpErrorHandler';
import PatientDataService from '../../../../services/patientData.service';
import IconButton from '../../../_controls/IconButton/IconButton';
import '../../Popup.sass';

const AddPatientImplantedDevice = () => {

    // <> Context
    const { addAlert, openPopup, openPopupState, setOpenPopup, setOpenPopupState } = useContext(MiscContext);

    // <> States
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(null);
    const [failure, setFailure] = useState(null);

    // <> History
    const history = useHistory();
    
    // <> Form
    const { handleSubmit, register, reset, formState: { errors } } = useForm();

    // <> Actions
    async function onSubmit(data) {
        try {

            // -> Turn Loader ON
            setIsLoading(true);

            // Params
            const params = { 
                patientId: openPopupState.patientId,
                date: moment(data.date).format(Configs.formats.apiDateTime),
                ...data
            }

            // -> Add
            await PatientDataService.add(Enums.PatientData.ImplantedDevices, params);
            
            // -> Callback
            setSuccess('Patient implanted device added successfully');
            if(openPopupState.onSuccess) openPopupState.onSuccess();

        }
        catch(e) {
            GlobalHttpErrorHandler(e, history, addAlert);
            setFailure(e.message);
        }
        finally {
            setIsLoading(false);
        }
    }

    // <> Helpers
    function closePopup() {
        setOpenPopup('');
        setOpenPopupState({});
        setSuccess('');
        setFailure('');
        reset();
    }

    function dismissFailure() {
        setFailure('');
    }

    // <> JSX
    return (
        <div className={`popup ${openPopup == Enums.PopupTags.AddPatientImplantedDevice ? 'popup--show' : ''}`}>

            {/* Overlay */}
            <div className="popup__overlay" onClick={closePopup}></div>

            {/* Container */}
            <div className="popup__container">

                {/* Title */}
                <div className="popup__container__title">Add Patient Implanted Device</div>
                <div className="popup__container__subtitle">for patient {openPopupState.patientName}</div>

                {openPopupState && <form onSubmit={handleSubmit(onSubmit)} className="popup__container__form">

                    {/* Device ID */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Device ID</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('deviceId', { required: 'device id required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.deviceId?.message}</label>
                    </div>

                    {/* Issuing Agency */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Issuing Agency</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('issuingAgency', { required: 'issuing agency required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.issuingAgency?.message}</label>
                    </div>

                    {/* Site Name */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Site Name</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('siteName', { required: 'site name required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.siteName?.message}</label>
                    </div>

                    {/* Site Code */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Site Code</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('siteCode', { required: 'site code required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.siteCode?.message}</label>
                    </div>

                    {/* Device Name */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Device Name</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('deviceName', { required: 'device name required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.deviceName?.message}</label>
                    </div>

                    {/* Device Description */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Device Description</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('deviceDescription', { required: 'device description required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.deviceDescription?.message}</label>
                    </div>

                    {/* Manufacturer */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Manufacturer</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('manufacturer', { required: 'manufacturer required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.manufacturer?.message}</label>
                    </div>

                    {/* Model */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Model</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('model', { required: 'model required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.model?.message}</label>
                    </div>

                    {/* Serial Number */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Serial Number</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('serialNumber', { required: 'serial number required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.serialNumber?.message}</label>
                    </div>

                    {/* lot */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">lot</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('lot', { required: 'lot required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.lot?.message}</label>
                    </div>

                    {/* isActive */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Is Active</label>
                        <input 
                            className="popup__container__form__input__control" 
                            {...register('isActive', { required: 'is active required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.isActive?.message}</label>
                    </div>

                    {/* Date */}
                    <div className="popup__container__form__input">
                        <label className="popup__container__form__input__label">Date</label>
                        <input 
                            className="popup__container__form__input__control" 
                            type="date"
                            {...register('date', { required: 'date required' })}
                            />
                        <label className="popup__container__form__input__error">{errors?.date?.message}</label>
                    </div>

                    {/* Submit Btn */}
                    <div className="popup__container__form__submit-btn">
                        <IconButton
                            text={'Add'}
                            icon={faPlus}
                            onClickFn={() => handleSubmit(onSubmit)}
                            isLoading={isLoading} />
                    </div>

                </form>}

                {/* Success Pane */}
                {success && (
                    <div className="popup__container__success-pane">
                        <FontAwesomeIcon icon={faCheckCircle} className="popup__container__success-pane__icon" />
                        <div className="popup__container__success-pane__message">{success}</div>
                        <button className="popup__container__success-pane__close-btn btn btn-lg btn-success" onClick={closePopup}>Close</button>
                    </div>
                )}

                {/* Failure Pane */}
                {failure && (
                    <div className="popup__container__failure-pane">
                        <FontAwesomeIcon icon={faExclamationCircle} className="popup__container__failure-pane__icon" />
                        <div className="popup__container__failure-pane__message">{failure}</div>
                        <button className="popup__container__failure-pane__close-btn btn btn-lg btn-danger" onClick={dismissFailure}>Back</button>
                    </div>
                )}

            </div>
        </div>
    );
};

export default AddPatientImplantedDevice;